/**
 * Turns on error reporting in the browser.
 *
 * Nothing private ever leaves the device: every report passes through the
 * scrubbers in sentry-scrubbers.ts, which strip journal writing, reflections,
 * assessment answers, emails and tokens before anything is sent. Only the
 * page, the error message, the time, the browser/device and a stack trace go
 * out.
 *
 * Reporting stays completely off unless a reporting address (SENTRY_DSN) is
 * configured, so local and preview work is unaffected.
 */
import { sentryContext } from "@/lib/sentry-context";
import { sentryScrubbers } from "@/lib/sentry-scrubbers";
import { sentryReplaySampleRates } from "@/lib/sentry-replay";
import { sentryBrowserPerformance } from "@/lib/sentry-performance";

let started = false;

declare const __SENTRY_DSN__: string | undefined;

function dsn(): string | undefined {
  try {
    return typeof __SENTRY_DSN__ === "string" && __SENTRY_DSN__.length > 0
      ? __SENTRY_DSN__
      : undefined;
  } catch {
    return undefined;
  }
}

/** Safe to call more than once; only the first call does anything. */
export async function startErrorReporting(): Promise<void> {
  if (started || typeof window === "undefined") return;
  const url = dsn();
  if (!url) return;
  started = true;

  try {
    const Sentry = await import("@sentry/react");
    const perf = sentryBrowserPerformance(Sentry as never);

    Sentry.init({
      dsn: url,
      environment: sentryContext.environment,
      release: sentryContext.release,
      integrations: [
        ...(perf.integrations as never[]),
        Sentry.replayIntegration({
          // Never record what members type or see in private areas.
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
        }),
      ],
      tracesSampleRate: perf.options.tracesSampleRate,
      tracePropagationTargets: perf.options.tracePropagationTargets,
      ...sentryReplaySampleRates,
      sendDefaultPii: false,
      // Strip anything private before a report leaves the device.
      beforeSend: (event) =>
        sentryScrubbers.beforeSend(event as never) as unknown as typeof event,
      beforeBreadcrumb: (crumb) =>
        sentryScrubbers.beforeBreadcrumb(crumb as never) as unknown as typeof crumb,
    });
  } catch (err) {
    // Reporting must never break the page for a member.
    console.warn("[monitoring] could not start error reporting", err);
    started = false;
  }
}
